import * as md5 from 'md5';
import * as browserStorage from 'data/browserStorage';
import { COOKIE_STORAGE, SESSION_STORAGE, TOKEN_KEY } from '../../data/browserStorage/constants';
import { UserRoles as bronteRoles } from '../../constants/enums';
import workspaceService from 'services/WorkspaceService';
import GTMResolver from './GTMResolver';
import { gtmTrackPageView, gtmTrackGlobalLaunchEventHandler, gtmTrackGlobalObligatoryLaunchEventHandler, gtmTrackUserExceptionEventHandler, gtmTrackUserInteractionActionEventHandler, convertKeysToCamelCase, trackUserInteractionActionEventHandler } from './gtmUtils';
import {
  baseGtmAnalyticsConstants,
  defaultGTMDataLayerPayload,
  GTM_EVENT_TYPES,
  GTM_CDP_EVENT_NAMES,
  GTM_CDP_TRACK_PAGEVIEW_EVENT,
  GTM_CDP_TRACK_USER_INTERACTION_EVENT,
  GTM_CDP_TRACK_USER_EXCEPTION_EVENT,
  TRACK_USER_INTERACTION,
} from './constants';
import { subscribeToEvent } from 'services/EventService';
import { clearCoursewareContext, getCoursewareContext } from 'utils/coursewareContext';

export const SESSION_ID = 'sessionId';
const GTM_USER_PARAMS = 'gtmUserParams';
const LOGIN_PAGE_TITLE = 'Login Page';
let gtmPluginEventBusSubscribed;

const cdpEventToGtmEventTypeMap = {
  [GTM_CDP_TRACK_PAGEVIEW_EVENT]: GTM_EVENT_TYPES.PAGEVIEW,
  [GTM_CDP_TRACK_USER_INTERACTION_EVENT]: GTM_EVENT_TYPES.USER_INTERACTION,
  [GTM_CDP_TRACK_USER_EXCEPTION_EVENT]: GTM_EVENT_TYPES.USER_EXCEPTION,
  [TRACK_USER_INTERACTION]: GTM_EVENT_TYPES.TRACK_USER_INTERACTION,
};

/**
 * Subscribes to plugin/CDP-emitted event names on the workspace EventService bus
 * (same bus CDP's EVENT_SERVICE delegates to). Avoids importing CDP/plugin-api
 * from workspace shell code, which would create a circular dependency.
 */
export function gtmCdpEventSubscriptionHandler(gtmTrackEventHandler) {
  if (typeof gtmTrackEventHandler !== 'function') {
    // eslint-disable-next-line no-console
    console.warn('[GTM][EventService] subscribe skipped (no gtmTrackEvent handler)');
    return;
  }

  if (gtmPluginEventBusSubscribed) {
    return;
  }

  for (const eventName of GTM_CDP_EVENT_NAMES) {
    subscribeToEvent(eventName, (payload) => {
      // eslint-disable-next-line no-console
      console.log('[GTM][EventService]', eventName, payload ?? '(no-payload)');
      const gtmEventType = cdpEventToGtmEventTypeMap[eventName];
      if (!gtmEventType) {
        // eslint-disable-next-line no-console
        console.warn(
          '[GTM][EventService] no GTM mapping for event',
          eventName,
          payload ?? '(no-payload)',
        );
        return;
      }

      gtmTrackEventHandler({ eventType: gtmEventType, payload: convertKeysToCamelCase(payload) ?? {} });
    });
  }

  gtmPluginEventBusSubscribed = true;
  // eslint-disable-next-line no-console
  console.log('[GTM][EventService] subscribed', GTM_CDP_EVENT_NAMES.length, GTM_CDP_EVENT_NAMES);
}

export default (() => {
  let gtmResolver;
  let isRegistered = false;
 
  // Caching the userParams here
  let _userParams;
  let _appInfo = { ...baseGtmAnalyticsConstants };
  let reloadObligatoryFired = false;

  const saveGtmUserParams = (userParams) => {
    browserStorage.save(GTM_USER_PARAMS, JSON.stringify(userParams), COOKIE_STORAGE);
  };

  const loadGtmUserParams = () => {
    const stored = browserStorage.load(GTM_USER_PARAMS, COOKIE_STORAGE);
    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  };

  const clearGtmUserParams = () => {
    browserStorage.remove(GTM_USER_PARAMS, COOKIE_STORAGE);
  };

  const buildUserParams = (currentUser, bearerToken) => ({
    personId: currentUser.accountId || null,
    personIdType: currentUser.authenticationType || defaultGTMDataLayerPayload.person_id_type,
    personRoleCode: getPearsonRoleCode(currentUser.roles),
    sessionId: getSessionIdFromBearerToken(bearerToken) || null,
    organizationId: currentUser.subscriptionId || null,
  });

  const fireObligatoryReload = (userParams) => {
    if (reloadObligatoryFired) {
      return;
    }

    reloadObligatoryFired = true;
    gtmTrackEvent({
      eventType: GTM_EVENT_TYPES.GLOBAL_OBLIGATORY_LAUNCH,
      payload: { event_action: 'reload', ...userParams },
    });
  };

  const getWorkspaceIdForAnalytics = () => {
    const { id } = workspaceService.getSelectedWorkspace() || {};
    return id || null;
  };

  /**
   * LoginPage can flash briefly during signed-in reload before the app reconnects.
   * Skip that pageview when a session reload is already in progress.
   */
  const shouldSuppressLoginPageViewDuringSessionReload = (eventPayload = {}) => {
    if (eventPayload.pageName !== LOGIN_PAGE_TITLE) {
      return false;
    }

    const bearerToken = browserStorage.load(TOKEN_KEY, COOKIE_STORAGE)
      || browserStorage.load(TOKEN_KEY, SESSION_STORAGE);

    if (!bearerToken) {
      return false;
    }

    return reloadObligatoryFired || Boolean(loadGtmUserParams());
  };

  /** Merges app, user + courseware context with the event-specific payload. */
  const buildEventPayload = (eventPayload = {}, { includeScreenId = true, includeGlobalContext = true } = {}) => {
    const user = _userParams || loadGtmUserParams() || {};
    const { courseId, lessonId, chapterId, screenId, sectionId } = getCoursewareContext();

    const globalContext = includeGlobalContext ? {
      app_id: _appInfo.app_id,
      app_version: _appInfo.app_version,
      platform: _appInfo.platform,
      personId: user.personId ?? null,
      personIdType: user.personIdType ?? null,
      personRoleCode: user.personRoleCode ?? null,
      sessionId: user.sessionId ?? null,
      organizationId: user.organizationId ?? null,
    } : {};

    return {
      ...globalContext,
      workspace_id: getWorkspaceIdForAnalytics(),
      courseId: courseId || null,
      lessonId: lessonId || null,
      chapterId: chapterId || null,
      sectionId: sectionId || null,
      ...(includeScreenId ? { screenId: screenId || null } : {}),
      ...eventPayload,
    };
  };

  /**
   * Method to set user params
   * @param currentUser - Object containing account info
   */
  const setUserParams = (currentUser) => {
    const { bearerToken } = gtmResolver.getBearerInfo();
    _userParams = buildUserParams(currentUser, bearerToken);
    saveGtmUserParams(_userParams);

    const { sessionId } = _userParams;
    const sessionIdFromCookie = browserStorage.load(SESSION_ID, COOKIE_STORAGE);
    if (!sessionIdFromCookie || sessionIdFromCookie !== sessionId) {
      // only run once when login is successful
      browserStorage.save(SESSION_ID, sessionId, COOKIE_STORAGE);
      gtmTrackEvent({
        eventType: GTM_EVENT_TYPES.GLOBAL_OBLIGATORY_LAUNCH,
        payload: { event_action: 'login', ..._userParams },
      });
    } else if (!reloadObligatoryFired) {
      fireObligatoryReload(_userParams);
    }
  };

 

  /**
   * Bronte roles to GTM roles
   * @param roles - One of INSTRUCTOR, AERO_INSTRUCTOR, STUDENT, STUDENT_GUEST, DEVELOPER, ADMIN, SUPPORT
   * @returns {string} - One of ["Student", "Instructor", "TeachingAssistant", "Parent", "Administrator","Employee", "Other"]
   */
  const getPearsonRoleCode = (roles = []) => {
    if (roles.includes(bronteRoles.SUPPORT)) {
      return 'Employee'; // Make consts in file
    }
    if (roles.includes(bronteRoles.ADMIN)) {
      return 'Administrator';
    }
    if (roles.includes(bronteRoles.DEVELOPER)) {
      return 'TeachingAssistant';
    }
    if (roles.includes(bronteRoles.AERO_INSTRUCTOR)) {
      return 'Instructor';
    }
    if (roles.includes(bronteRoles.STUDENT || roles.includes(bronteRoles.STUDENT_GUEST))) {
      return 'STUDENT';
    }
    return 'OTHER';
  };

  const getSessionIdFromBearerToken = (bearerToken) => md5(bearerToken);

  /**
   * Method to push users logout event
   */
  const pushUserLogoutEvent = () => {
    // Clear cached values on logout
    _userParams = undefined;
    reloadObligatoryFired = false;
    clearGtmUserParams();
    clearCoursewareContext();
    gtmTrackEvent({ eventType: GTM_EVENT_TYPES.GLOBAL_OBLIGATORY_LAUNCH, payload: { event_action: 'logout'} });

  };

  function gtmTrackEvent({ eventType, payload }) {
    switch (eventType) {
      case GTM_EVENT_TYPES.GLOBAL_LAUNCH:
        gtmTrackGlobalLaunchEventHandler(payload);
        return;

      case GTM_EVENT_TYPES.GLOBAL_OBLIGATORY_LAUNCH:
        gtmTrackGlobalObligatoryLaunchEventHandler(payload);
        return;

      case GTM_EVENT_TYPES.PAGEVIEW:
        if (shouldSuppressLoginPageViewDuringSessionReload(payload)) {
          return;
        }
        gtmTrackPageView(buildEventPayload(payload, { includeScreenId: false, includeGlobalContext: false }));
        return;

      case GTM_EVENT_TYPES.USER_INTERACTION:
        gtmTrackUserInteractionActionEventHandler(buildEventPayload(payload, { includeGlobalContext: false }));
        return;

      case GTM_EVENT_TYPES.TRACK_USER_INTERACTION:
        trackUserInteractionActionEventHandler(buildEventPayload(payload, { includeGlobalContext: false }));
        return;

      case GTM_EVENT_TYPES.USER_EXCEPTION:
        gtmTrackUserExceptionEventHandler(buildEventPayload(payload, { includeGlobalContext: false }));
        return;
      default:
        // eslint-disable-next-line no-console
        console.warn("Unknown GTM event type:", eventType);
        return;
    }
  }

  const onReloadCaptureObligatoryParameters = (browserParams) => {
    _appInfo = {
      app_id: browserParams?.app_id ?? baseGtmAnalyticsConstants.app_id,
      app_version: browserParams?.app_version ?? baseGtmAnalyticsConstants.app_version,
      platform: baseGtmAnalyticsConstants.platform,
    };

    const bearerToken = browserStorage.load(TOKEN_KEY, COOKIE_STORAGE)
      || browserStorage.load(TOKEN_KEY, SESSION_STORAGE);

    if (bearerToken) {
      const storedParams = loadGtmUserParams();
      if (storedParams) {
        fireObligatoryReload({
          ...storedParams,
          sessionId: getSessionIdFromBearerToken(bearerToken),
        });
      }
      return;
    }

    gtmTrackEvent({ eventType: GTM_EVENT_TYPES.GLOBAL_LAUNCH, payload: browserParams });
  };

  return {
    isRegistered: () => isRegistered,
    register: (connection) => {
      isRegistered = true;
      gtmResolver = new GTMResolver(connection);
    },
    onReload:onReloadCaptureObligatoryParameters,
    setUserParams, // (login)
    pushUserLogoutEvent, // (logout)
    gtmTrackEvent, // (pageview, user_interaction, user_exception)
    gtmCdpEventSubscriptionHandler,
  };
})();
